import {
  CallDirection,
  CallState,
  UserCallAvailability,
} from "@redotech/redo-support-common/support/voice/voice-types";
import { memo, useContext, useEffect, useState } from "react";
import { Flex } from "../flex";
import { REDO_RINGTONE } from "../sound/sounds";
import { ActiveCallView } from "./active-call-view";
import { CollapsedPhoneWidget } from "./collapsed-phone-widget";
import { InboundCall } from "./inbound-call";
import { PhoneIdle } from "./phone-idle";
import * as phoneWidgetCss from "./phone-widget.module.css";
import { usePhoneWidget } from "./shared/phone-widget-context";
import { ExpandedState, PhoneWidgetHeader } from "./shared/phone-widget-header";
import { VoiceContext } from "./voice-context";

/**
 * Requires `VoiceContext` and `PhoneWidgetContext`
 */
export const PhoneWidget = memo(function PhoneWidget({
  status,
  merchantPhoneNumber,
}: {
  status: UserCallAvailability;
  merchantPhoneNumber: string;
}) {
  const { call } = useContext(VoiceContext);
  const { isVisible, hide } = usePhoneWidget();
  const [expandedState, setExpandedState] =
    useState<ExpandedState>("collapsed");

  useEffect(() => {
    if (call?.state === CallState.RINGING) {
      REDO_RINGTONE.play().catch((error) => {
        console.warn("Failed to play ringing sound:", error);
      });
    } else {
      REDO_RINGTONE.pause();
      REDO_RINGTONE.currentTime = 0;
    }
  }, [call?.state, call?.direction]);

  if (!isVisible) {
    return null;
  }

  return (
    <Flex className={phoneWidgetCss.widgetContainer} dir="column">
      {expandedState === "collapsed" && (
        <CollapsedPhoneWidget
          onClose={hide}
          onExpand={() => setExpandedState("full")}
          status={status}
        />
      )}
      {expandedState === "full" && (
        <ExpandedPhoneWidget
          merchantPhoneNumber={merchantPhoneNumber}
          onClose={hide}
          setExpandedState={setExpandedState}
        />
      )}
    </Flex>
  );
});

const ExpandedPhoneWidget = memo(function ExpandedPhoneWidget({
  onClose,
  setExpandedState,
  merchantPhoneNumber,
}: {
  onClose(): void;
  setExpandedState: (val: ExpandedState) => void;
  merchantPhoneNumber: string;
}) {
  const { call, performCall, status, setStatus } = useContext(VoiceContext);

  const isInboundRinging =
    call &&
    (call.state === CallState.RINGING || call.state === CallState.CONNECTING) &&
    call.direction === CallDirection.INBOUND;

  return (
    <Flex className={phoneWidgetCss.bodySections} dir="column" gap="none">
      <PhoneWidgetHeader
        merchantPhoneNumber={merchantPhoneNumber}
        onClose={onClose}
        setExpandedState={setExpandedState}
      />
      {!call && (
        <PhoneIdle
          performCall={performCall}
          setStatus={setStatus}
          status={status}
        />
      )}
      {isInboundRinging && <InboundCall call={call} />}
      {call && !isInboundRinging && <ActiveCallView call={call} />}
    </Flex>
  );
});

// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { type ExpandedState } from "./shared/phone-widget-header";
